import { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Check,
  Inventory2Outlined,
  Restore,
} from "@mui/icons-material";
import { useGetOne, useNotify, useRecordContext, useRefresh } from "react-admin";
import {
  AxiosError,
  TProduct,
  TProductSupplier,
  TSupplier,
  getSeeriApi,
} from "@/api";
import { useAdmin } from "@/hooks/admin";
import { SUPPLIERS } from "@/config/resources";

type Row = {
  productId: string;
  supplierId: string;
  warehouseId: string;
  warehouseName: string;
  available: number;
  reserved: number;
};

const flattenWarehouses = (product?: TProduct): Row[] => {
  if (!product?.suppliers) return [];
  return product.suppliers.flatMap((s: TProductSupplier) =>
    (s.warehouses ?? []).map((w: any) => ({
      productId: product.id,
      supplierId: s.supplierId,
      warehouseId: w.warehouseId ?? w.id,
      warehouseName: w.warehouseName ?? "—",
      available: Number(w.availableStock ?? 0),
      reserved: Number(w.reservedStock ?? 0),
    }))
  );
};

const postMovement = (
  productId: string,
  supplierId: string,
  body: Record<string, unknown>
) =>
  getSeeriApi().post(`/api/products/v5/${productId}/movements`, body, {
    headers: { "x-supplier-id": supplierId },
  });

export const WarehouseStockTable: FC = () => {
  const product = useRecordContext<TProduct>();
  const { hasPermission } = useAdmin();
  const [addOpen, setAddOpen] = useState(false);

  const productSupplier = product?.suppliers?.[0];
  const rows = useMemo(() => flattenWarehouses(product), [product]);

  if (!product) return null;

  const canEdit = hasPermission("product.update");

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ mb: 1 }}
      >
        {canEdit && productSupplier && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setAddOpen(true)}
            sx={{
              "& .MuiButton-startIcon": {
                mr: { xs: 0, sm: 1 },
              },
              "& .button-label": {
                display: { xs: "none", sm: "inline" },
              },
              minWidth: { xs: 36, sm: "auto" },
              px: { xs: 1, sm: 2 },
            }}
          >
            <span className="button-label">Agregar stock a bodega</span>
          </Button>
        )}
      </Stack>

      {rows.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 4,
            color: "text.secondary",
          }}
        >
          <Inventory2Outlined sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            Este producto aún no tiene stock asignado a bodegas.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table
            size="small"
            sx={{
              tableLayout: "auto",
              "& td, & th": {
                px: { xs: 0.5, sm: 1.5 },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Bodega</TableCell>
                <TableCell
                  align="right"
                  sx={{ display: { xs: "none", sm: "table-cell" } }}
                >
                  Reservado
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ width: { xs: 130, sm: 220 } }}
                >
                  Disponible
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <WarehouseStockRow
                  key={`${row.supplierId}-${row.warehouseId}`}
                  row={row}
                  canEdit={canEdit}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {productSupplier && (
        <AddWarehouseStockDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          productId={product.id}
          productSupplier={productSupplier}
          existingWarehouseIds={rows.map((r) => r.warehouseId)}
        />
      )}
    </Box>
  );
};

const WarehouseStockRow: FC<{ row: Row; canEdit: boolean }> = ({
  row,
  canEdit,
}) => {
  const [value, setValue] = useState<string>(String(row.available));
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const numeric = Number(value);
  const dirty = !Number.isNaN(numeric) && numeric !== row.available;

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await postMovement(row.productId, row.supplierId, {
        amount: numeric,
        destiny: "AVAILABLE",
        type: "PROVISIONING",
        warehouseId: row.warehouseId,
        force: true,
      });
      notify("Stock actualizado", { type: "success" });
      refresh();
    } catch (e) {
      const msg =
        e instanceof AxiosError ? e.response?.data?.message : String(e);
      notify(msg ?? "Error al actualizar stock", { type: "error" });
      setValue(String(row.available));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow hover>
      <TableCell>{row.warehouseName}</TableCell>
      <TableCell
        align="right"
        sx={{ display: { xs: "none", sm: "table-cell" } }}
      >
        {row.reserved}
      </TableCell>
      <TableCell align="right">
        <TextField
          size="small"
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!canEdit || saving}
          inputProps={{
            min: 0,
            style: { textAlign: "right" },
          }}
          sx={{ width: { xs: 120, sm: 180 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {dirty ? (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Descartar">
                      <IconButton
                        size="small"
                        onClick={() => setValue(String(row.available))}
                        disabled={saving}
                      >
                        <Restore fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        numeric > row.available
                          ? `Sumar ${numeric - row.available}`
                          : `Restar ${row.available - numeric}`
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={save}
                          disabled={saving}
                        >
                          <Check fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : null}
              </InputAdornment>
            ),
          }}
        />
      </TableCell>
    </TableRow>
  );
};

const AddWarehouseStockDialog: FC<{
  open: boolean;
  onClose: () => void;
  productId: string;
  productSupplier: TProductSupplier;
  existingWarehouseIds: string[];
}> = ({ open, onClose, productId, productSupplier, existingWarehouseIds }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [warehouseId, setWarehouseId] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: supplier } = useGetOne<TSupplier>(
    SUPPLIERS,
    { id: productSupplier.supplierId },
    { enabled: open && !!productSupplier.supplierId }
  );

  const addresses = (supplier as any)?.addresses ?? [];

  const reset = () => {
    setWarehouseId("");
    setAmount("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!warehouseId || !amt || amt <= 0 || saving) return;
    setSaving(true);
    try {
      await postMovement(productId, productSupplier.supplierId, {
        amount: amt,
        destiny: "AVAILABLE",
        type: "PROVISIONING",
        warehouseId,
      });
      notify("Stock agregado", { type: "success" });
      refresh();
      close();
    } catch (e) {
      const msg =
        e instanceof AxiosError ? e.response?.data?.message : String(e);
      notify(msg ?? "Error al agregar stock", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Agregar stock a bodega</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label="Bodega"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            fullWidth
            helperText={
              addresses.length === 0
                ? "El distribuidor no tiene bodegas registradas"
                : "Las bodegas con stock ya cargado se marcan abajo."
            }
          >
            {addresses.map((a: any) => {
              const label = a.name ?? a.addressLine1 ?? a.id;
              const existing = existingWarehouseIds.includes(a.id);
              return (
                <MenuItem key={a.id} value={a.id}>
                  {label}
                  {existing && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (ya tiene stock)
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            type="number"
            label="Cantidad a agregar"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!warehouseId || !Number(amount) || saving}
        >
          Agregar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
